import { AsyncFunction } from "../../types/functions.ts";
import { useEffect, useState } from "react";
import { logError } from "../../errorHandling/errorLogging.ts";
import { AsyncState, useAsyncState } from "./asyncState.ts";

export interface UseParameterizedAsyncDataOptions {
	fetchOnMount?: boolean;
	afterRefresh?: () => void;
}

export interface UseParameterizedAsyncDataReturn<Result, ParamType extends object> {
	dataAsync: AsyncState<Result>;
	paramValues: ParamType;
	refresh: (newParams?: Partial<ParamType>) => Promise<Result>;
	setLocalData: (setter: (data: Result) => Result) => void;
}

export const useParameterizedAsyncData = <Result, ParamType extends object>(
	fetchData: AsyncFunction<ParamType, Result>,
	initialParams: ParamType,
	{ afterRefresh, fetchOnMount = true }: UseParameterizedAsyncDataOptions = {},
): UseParameterizedAsyncDataReturn<Result, ParamType> => {
	const [paramValues, setParamValues] = useState(initialParams);
	const [dataAsync, setDataAsync, setDataImmediate] = useAsyncState<Result>();

	const refresh = async (newParams: Partial<ParamType> = {}) => {
		setParamValues((currentParams) => {
			return {
				...currentParams,
				...newParams,
			};
		});
		const result = await setDataAsync(() =>
			fetchData({
				...paramValues,
				...newParams,
			}),
		);
		afterRefresh?.();
		return result;
	};

	useEffect(() => {
		if (fetchOnMount) {
			refresh({}).catch(logError);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		dataAsync: dataAsync,
		paramValues: paramValues,
		refresh: refresh,
		setLocalData: setDataImmediate,
	};
};
